import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CrmEmptyState } from '../crm/CrmEmptyState';
import { CrmMobileTabs } from '../crm/CrmMobileTabs';
import { useCrmHost } from '../crm/CrmHostContext';
import { CrmLoggedOutBanner } from '../crm/CrmLoggedOutBanner';
import { CrmPageRail } from '../crm/CrmPageRail';
import { CrmTopBar } from '../crm/CrmTopBar';
import { DEFAULT_CRM_PAGE, isCrmPage } from '../crm/crmBridge';
import { canRead, canWrite, isFanslyOnlyPage, isOnlyFansOnlyPage, isOwnerOf, type MenuStyle } from '../crm/crmRail';
import { useStyling } from '../theme/userStyling';
import { FanslyPageHost } from '../fansly/FanslyPageHost';
import { useMediaGuard } from '../crm/mediaGuard';
import { AccountFanslyDeps } from '../fansly/FanslyDeps';
import { MePic } from '../fansly/ui/MePic';
import { useCrmPermissions } from '../crm/useCrmPermissions';
import { ClaimBottomBar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { useAccounts } from '../context/AccountsContext';
import { AccountChats } from '../chats/AccountChats';
import { AccountLibraryDeps } from '../library/AccountLibraryDeps';
import { CollectionsPage } from '../library/collections/CollectionsPage';
import { VaultPage } from '../library/vault/VaultPage';
import { BankingPage } from '../of/banking/BankingPage';
import { MorePage, MorePopup } from '../of/banking/MorePage';
import { AccountCrmPageDeps } from '../of/CrmRequest';
import { HomePage } from '../of/home/HomePage';
import { NotificationsPage } from '../of/notifications/NotificationsPage';
import { ProfilePage } from '../of/profile/ProfilePage';
import { QueuePage } from '../of/queue/QueuePage';
import { CardsPage } from '../of/more/CardsPage';
import { DiscoverPage } from '../of/more/DiscoverPage';
import type { MorePageProps } from '../of/more/morePage';
import { ReferralsPage } from '../of/more/ReferralsPage';
import { ReleaseFormsPage } from '../of/more/ReleaseFormsPage';
import { StreamingPage } from '../of/more/StreamingPage';
import { SettingsPage } from '../of/settings/SettingsPage';
import { StatementsPage } from '../of/statements/StatementsPage';
import { StatisticsPage } from '../of/statistics/StatisticsPage';

/** OnlyFans pages reached from More, each a React page of its own. */
const MORE_PAGES: Record<string, (props: MorePageProps) => React.ReactNode> = {
  streaming: StreamingPage,
  referrals: ReferralsPage,
  discover: DiscoverPage,
  releaseforms: ReleaseFormsPage,
  cards: CardsPage,
};

/**
 * The CRM view of one account: /account/:accountId/crm/:page. The page itself
 * is the persistent CRM frame (CrmFrameHost) pinned over the main area; this
 * component owns the URL, the shell around it, and the states where the frame
 * cannot be shown (no CRM access for this login or account, CRM unreachable).
 * The CRM page rail is the console's (ADR 0023); every OnlyFans page but
 * Chats (behind its stage gate) and Mass is a React page rendered here
 * instead of the frame. On phones the rail becomes the bottom tab bar
 * (CrmMobileTabs).
 */
export function AccountCrmPage() {
  const { t } = useTranslation('common');
  const { accountId, page } = useParams<{ accountId: string; page?: string }>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { fanslyMenu } = useStyling();
  const crm = useCrmHost();
  const perms = useCrmPermissions(accountId);
  // Media download (CONTEXT.md): without it, the account's media is guarded on its React pages.
  useMediaGuard(accountId, perms);
  const { user } = useAuth();
  const { accounts: consoleAccounts } = useAccounts();
  // Bumped after a re-login so the page on screen asks OnlyFans again.
  const [reloads, setReloads] = useState(0);
  // More opened from the rail as OnlyFans' popup: the account it belongs to and the card's left edge.
  const [more, setMore] = useState<{ accountId: string; left: number } | null>(null);

  if (!accountId) return <Navigate to="/dashboard" replace />;
  // One canonical URL per page so back/forward and reload land on the same view.
  if (!page || !isCrmPage(page) || page === 'dashboard') {
    return <Navigate to={`/account/${encodeURIComponent(accountId)}/crm/${DEFAULT_CRM_PAGE}`} replace />;
  }

  const account = crm.crmAccounts.find((a) => a.account_id === accountId);
  const platform = account?.platform;
  // Fansly's own navigation (a top bar), for a person who picked it, on Fansly accounts showing the React pages (ADR 0036).
  const menu: MenuStyle = platform === 'fansly' && fanslyMenu === 'fansly' && crm.rendersReact(accountId, 'home') ? 'fansly' : 'onlyfans';
  const mobileNav = <CrmMobileTabs accountId={accountId} page={page} perms={perms} unread={crm.unread[accountId]} menu={menu} />;

  const openPage = (p: string, query?: string, opts?: { replace?: boolean }) =>
    navigate(`/account/${encodeURIComponent(accountId)}/crm/${p}${query ? `?${query}` : ''}`, { replace: opts?.replace });

  const openAi = { label: t('crmFrame.openAiView'), onPress: () => navigate(`/account/${encodeURIComponent(accountId)}`) };

  let notice: React.ReactNode = null;
  if (crm.access.state === 'error') {
    notice = <CrmEmptyState icon="alert-circle" title={t('crmFrame.unavailableTitle')} body={t('crmFrame.unavailableBody')} action={openAi} />;
  } else if (crm.access.state === 'none') {
    notice = <CrmEmptyState icon="lock" title={t('crmFrame.noAccessTitle')} body={t('crmFrame.noAccessBody')} action={openAi} />;
  } else if (crm.access.state === 'ok' && (!crm.hasCrmAccess(accountId) || crm.frameDenied)) {
    notice = <CrmEmptyState icon="lock" title={t('crmFrame.noAccountAccessTitle')} body={t('crmFrame.noAccountAccessBody')} action={openAi} />;
  } else if (crm.isPlanLocked(accountId)) {
    // No-plan lock: whoever can pay picks a plan in Payments; anyone else asks the owner.
    const canPay = !!user?.can_access_payments && consoleAccounts.some((a) => a.account_id === accountId);
    notice = canPay ? (
      <CrmEmptyState
        icon="lock"
        title={t('crmFrame.noPlanTitle')}
        body={t('crmFrame.noPlanBody')}
        action={{ label: t('crmFrame.choosePlan'), onPress: () => navigate(`/payments?account=${encodeURIComponent(account?.username || accountId)}`) }}
      />
    ) : (
      <CrmEmptyState icon="lock" title={t('crmFrame.noPlanTitle')} body={t('crmFrame.noPlanBodyAskOwner')} />
    );
  }

  if (notice) return notice;

  // The Fansly tools under More exist only for Fansly accounts.
  if (platform && platform !== 'fansly' && isFanslyOnlyPage(page)) {
    return <Navigate to={`/account/${encodeURIComponent(accountId)}/crm/more`} replace />;
  }
  // …and OnlyFans' own More pages only for OnlyFans accounts.
  if (platform === 'fansly' && isOnlyFansOnlyPage(page)) {
    return <Navigate to={`/account/${encodeURIComponent(accountId)}/crm/more`} replace />;
  }

  // A Fansly account renders its React pages (ADR 0036) or else the embedded Fansly workspace.
  const reactPage = crm.rendersReact(accountId, page) ? page : null;
  const pageKey = `${accountId}:${reloads}`;
  const onReconnected = () => {
    if (reactPage) setReloads((n) => n + 1);
    else crm.reloadFrame();
  };

  return (
    <>
      {/* Phones: the CRM tab bar (CrmMobileTabs, with the Menu tab) replaces the shell's. */}
      <ClaimBottomBar />
      {menu === 'fansly' ? (
        // The account's picture comes from Fansly itself (account/me): the stored one is a link Fansly lets expire.
        <AccountFanslyDeps accountId={accountId}>
          <CrmTopBar
            accountId={accountId}
            page={page}
            perms={perms}
            unread={crm.unread[accountId]}
            account={account && { name: account.profile_name || account.username || accountId, username: account.username, avatarUrl: account.avatar_url }}
            picture={(className) => <MePic name={account?.profile_name || account?.username || accountId} fallbackUrl={account?.avatar_url} className={className} />}
          />
        </AccountFanslyDeps>
      ) : (
        <CrmPageRail
          accountId={accountId}
          page={page}
          perms={perms}
          unread={crm.unread[accountId]}
          moreOpen={more?.accountId === accountId}
          onMore={(railRight) => setMore((m) => (m?.accountId === accountId ? null : { accountId, left: railRight + 16 }))}
        />
      )}
      {more?.accountId === accountId && menu === 'onlyfans' && (
        <AccountLibraryDeps accountId={accountId}>
          <MorePopup
            accountId={accountId}
            left={more.left}
            canOpen={(p) => isCrmPage(p) && canRead(perms, p)}
            onOpenPage={openPage}
            onClose={() => setMore(null)}
          />
        </AccountLibraryDeps>
      )}
      <CrmLoggedOutBanner accountId={accountId} onReconnected={onReconnected} topBar={menu === 'fansly'} />
      {reactPage && platform === 'fansly' ? (
        <FanslyPageHost
          key={pageKey}
          accountId={accountId}
          page={reactPage}
          perms={perms}
          canRead={(p) => canRead(perms, p)}
          canWrite={(p) => canWrite(perms, p)}
          query={search.replace(/^\?/, '')}
          onOpenPage={(p, query, opts) => navigate(`/account/${encodeURIComponent(accountId)}/crm/${p}${query ? `?${query}` : ''}`, { replace: opts?.replace })}
          onOpenChat={(fanId) => crm.openFanChat(accountId, fanId)}
          reportUnread={(count) => crm.reportUnread(accountId, count)}
          mobileNav={mobileNav}
        />
      ) : reactPage === 'chats' ? (
        <AccountChats
          key={pageKey}
          accountId={accountId}
          canEditTabs={canWrite(perms, 'collections')}
          can={{
            writeChats: canWrite(perms, 'chats'),
            // users/restrict: any of Chats, Collections or Profile write (routePermissions.js).
            restrict: canWrite(perms, 'chats') || canWrite(perms, 'collections') || canWrite(perms, 'profile'),
          }}
          canReadStats={canRead(perms, 'statistics')}
          mobileNav={mobileNav}
        />
      ) : reactPage === 'vault' ? (
        <AccountLibraryDeps accountId={accountId}>
          <VaultPage key={pageKey} accountId={accountId} canWrite={canWrite(perms, 'vault')} mobileNav={mobileNav} />
        </AccountLibraryDeps>
      ) : reactPage === 'home' ? (
        <AccountLibraryDeps accountId={accountId}>
          <HomePage
            key={pageKey}
            accountId={accountId}
            canWrite={canWrite(perms, 'home')}
            onOpenPage={openPage}
            mobileNav={mobileNav}
          />
        </AccountLibraryDeps>
      ) : reactPage === 'collections' ? (
        <AccountLibraryDeps accountId={accountId}>
          <CollectionsPage
            key={pageKey}
            accountId={accountId}
            canWrite={canWrite(perms, 'collections')}
            onOpenChat={(fanId) => crm.openFanChat(accountId, String(fanId))}
            mobileNav={mobileNav}
            query={search.replace(/^\?/, '')}
          />
        </AccountLibraryDeps>
      ) : reactPage === 'profile' ? (
        <AccountLibraryDeps accountId={accountId}>
          <ProfilePage
            key={pageKey}
            accountId={accountId}
            canWrite={canWrite(perms, 'profile')}
            onOpenPage={openPage}
            mobileNav={mobileNav}
          />
        </AccountLibraryDeps>
      ) : reactPage === 'queue' ? (
        <AccountLibraryDeps accountId={accountId}>
          <QueuePage key={pageKey} accountId={accountId} canWrite={canWrite(perms, 'queue')} mobileNav={mobileNav} />
        </AccountLibraryDeps>
      ) : reactPage === 'notifications' ? (
        <AccountCrmPageDeps accountId={accountId}>
          <NotificationsPage
            key={pageKey}
            accountId={accountId}
            canWrite={canWrite(perms, 'notifications')}
            onOpenChat={(fanId) => crm.openFanChat(accountId, String(fanId))}
            mobileNav={mobileNav}
          />
        </AccountCrmPageDeps>
      ) : reactPage === 'statements' ? (
        <AccountCrmPageDeps accountId={accountId}>
          <StatementsPage
            key={pageKey}
            accountId={accountId}
            canWrite={canWrite(perms, 'statements')}
            canBank={canRead(perms, 'banking')}
            onOpenPage={openPage}
            onOpenChat={(fanId) => crm.openFanChat(accountId, String(fanId))}
            mobileNav={mobileNav}
          />
        </AccountCrmPageDeps>
      ) : reactPage === 'statistics' ? (
        <AccountCrmPageDeps accountId={accountId}>
          <StatisticsPage
            key={pageKey}
            accountId={accountId}
            canWritePayouts={canWrite(perms, 'statements')}
            onOpenPage={openPage}
            onOpenChat={(fanId) => crm.openFanChat(accountId, String(fanId))}
            mobileNav={mobileNav}
            query={search.replace(/^\?/, '')}
          />
        </AccountCrmPageDeps>
      ) : reactPage === 'more' ? (
        <AccountLibraryDeps accountId={accountId}>
          <MorePage
            key={pageKey}
            accountId={accountId}
            canOpen={(p) => isCrmPage(p) && canRead(perms, p)}
            onOpenPage={openPage}
            mobileNav={mobileNav}
          />
        </AccountLibraryDeps>
      ) : reactPage === 'settings' ? (
        <AccountLibraryDeps accountId={accountId}>
          <SettingsPage
            key={pageKey}
            accountId={accountId}
            canWrite={canWrite(perms, 'settings')}
            isOwner={isOwnerOf(perms)}
            query={search.replace(/^\?/, '')}
            onOpenPage={openPage}
            mobileNav={mobileNav}
          />
        </AccountLibraryDeps>
      ) : reactPage && MORE_PAGES[reactPage] ? (
        perms && !canRead(perms, reactPage as Parameters<typeof canRead>[1]) ? (
          <Navigate to={`/account/${encodeURIComponent(accountId)}/crm/more`} replace />
        ) : (
          <AccountCrmPageDeps accountId={accountId}>
            {(() => {
              const MorePageView = MORE_PAGES[reactPage];
              return (
                <MorePageView
                  key={pageKey}
                  accountId={accountId}
                  canWrite={canWrite(perms, reactPage as Parameters<typeof canWrite>[1])}
                  query={search.replace(/^\?/, '')}
                  onOpenPage={openPage}
                  onOpenChat={(fanId) => crm.openFanChat(accountId, String(fanId))}
                  mobileNav={mobileNav}
                />
              );
            })()}
          </AccountCrmPageDeps>
        )
      ) : reactPage === 'banking' ? (
        perms && !canRead(perms, 'banking') ? (
          <Navigate to={`/account/${encodeURIComponent(accountId)}/crm/more`} replace />
        ) : (
          <AccountCrmPageDeps accountId={accountId}>
            <BankingPage key={pageKey} accountId={accountId} onBack={() => openPage('more')} mobileNav={mobileNav} />
          </AccountCrmPageDeps>
        )
      ) : (
        <>
          <div className="crm-page-filler" aria-hidden="true" style={{ minHeight: 'calc(100 * var(--om-vh))' }} />
          {/* Phones: the rail is hidden and the frame hides the CRM's own
              Sidebar, so the tab bar docks under the frame to switch pages. */}
          <div className="crm-mdock">{mobileNav}</div>
        </>
      )}
    </>
  );
}
